import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import crosshair from 'chartjs-plugin-crosshair';
import annotationPlugin from 'chartjs-plugin-annotation';

import './Charts.css';

const legendMargin = {
  id : 'legendMargin',
  beforeInit: function(chart, legend, options) {
    const fitValue = chart.legend.fit;
    chart.legend.fit = function fit() {
      fitValue.bind(chart.legend)();
      return this.height += 16;
    };
  }
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  legendMargin,
  annotationPlugin
);

const SmoothLineChart = forwardRef(({
  data,
  title = '',
  height = '300px',
  width = '100%',
  showLegend = true,
  showGrid = true,
  responsive = true,
  maintainAspectRatio = false,
  className = '',
  lastUpdateTime = null,
  drDispatchInfo = null,
  chartBorder = true,
  chartPadding = true,
  isToday = false,
  yMin = null
}, ref) => {
  const chartRef = useRef(null);
  const previousDataRef = useRef(null);
  
  // lastUpdateTime을 x축 인덱스로 변환
  const getLastUpdateIndex = () => {
    if (!lastUpdateTime || !data?.labels) return null;
    
    const lastUpdateHour = lastUpdateTime.hour();
    const lastUpdateMinute = lastUpdateTime.minute();
    const lastUpdateIndex = lastUpdateHour * 4 + (lastUpdateMinute / 15);
    
    // 해당 시간의 라벨 찾기
    const timeLabel = `${lastUpdateHour.toString().padStart(2, '0')}:${lastUpdateMinute.toString().padStart(2, '0')}`;
    const index = data.labels.findIndex(label => label === timeLabel);
    
    return index >= 0 ? index : null;
  };
  
  // 부드러운 업데이트를 위한 메서드 노출
  useImperativeHandle(ref, () => ({
    updateChart: (newData) => {
      if (chartRef.current && chartRef.current.chartInstance) {
        const chart = chartRef.current.chartInstance;
        
        // 데이터가 실제로 변경되었는지 확인
        if (JSON.stringify(previousDataRef.current) !== JSON.stringify(newData)) {
          // 부드러운 애니메이션으로 데이터 업데이트
          chart.data = newData;
          chart.update('active'); // 'active' 모드로 부드럽게 업데이트
          previousDataRef.current = newData;
        }
      }
    },
    getChart: () => chartRef.current?.chartInstance
  }));

  const [cssColors, setCssColors] = useState({
    gray600: '#718096'
  });
  // CSS 변수에서 색상 값 가져오기
  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };

    setCssColors({
      gray600: getCSSVariable('--gray-600')
    });
  }, []);

  // x축 라벨 색상 설정 x=0 위치의 라벨 색상
  const getXAxisLabelColor = (context) => {
    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600;
    }
    return 'transparent';
  };
  // y축 라벨 색상 설정 y=0 위치의 라벨 색상
  const getYAxisLabelColor = (context) => {
    if (context.tick && context.tick.value === 0) {
      return cssColors.gray600;
    }
    return 'transparent';
  };

  const chartData = {
    labels: data?.labels || [],
    datasets: data?.datasets?.map(dataset => ({
      ...dataset,
      spanGaps: true // null 값을 건너뛰고 선을 연결
    })) || []
  };

  // 이전 데이터 저장
  useEffect(() => {
    previousDataRef.current = chartData;
  }, [chartData]);

  const options = {
    responsive,
    maintainAspectRatio,
    layout: {
      padding: {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      }
    },

    scales: {
      x: {
        display: true,
        title: {
          display: true,
          color: cssColors.gray600,
          font: {
            size: 12
          }
        },
        grid: {
          display: showGrid,
          color: getXAxisLabelColor,
          drawBorder: chartBorder
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          maxTicksLimit: 24,  // 24시간 표시
          callback: function(value, index, values) {
            // 2시간마다만 라벨 표시 (00:00, 02:00, 04:00...)
            if (index % 4 === 0) {
              return this.getLabelForValue(value);
            }
            return '';
          }
        },
      },
      y: {
        display: true,
        min: yMin !== null ? yMin : undefined,
        title: {
          display: true,
          color: cssColors.gray600,
          font: {
            size: 12
          }
        },
        grid: {
          display: showGrid,
          color: getYAxisLabelColor,
          drawBorder: chartBorder
        },
        ticks: {
          color: cssColors.gray600,
          font: {
            size: 11
          },
          callback: function(value) {
            return value + ' kWh';
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        align: 'start',
        labels: {
          usePointStyle: true,
          boxHeight: 5,
          boxWidth: 5,
          font: {
            size: 12,
            weight: '700'
          },
          color: cssColors.gray600
        },
      },
      annotation: {
        annotations: {
          ...(lastUpdateTime ? {
            lastUpdateLine: {
              type: 'line',
              xMin: getLastUpdateIndex(),
              xMax: getLastUpdateIndex(),
              borderColor: '#FF6B6B',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                content: '마지막 집계',
                enabled: true,
                position: 'start',
                backgroundColor: '#FF6B6B',
                color: 'white',
                font: {
                  size: 10,
                  weight: 'bold'
                },
                padding: 4,
                rotation: 90
              }
            }
          } : {}),
          ...(drDispatchInfo && isToday ? {
            drDispatchBox: {
              type: 'box',
              xMin: drDispatchInfo.startIndex,
              xMax: drDispatchInfo.endIndex + 1,
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              borderColor: 'rgba(239, 68, 68, 0.5)',
              borderWidth: 0,
              borderRadius: 10,
              label: {
                content: 'DR 발령',
                enabled: true,
                position: 'start',
                backgroundColor: 'rgba(239, 68, 68, 0.8)',
                color: 'white',
                font: {
                  size: 10,
                  weight: 'bold'
                },
                padding: 4,
                rotation: 90
              }
            }
          } : {})
        }
      },
      
      crosshair: {
        line: {
          color: '#f44336',
          width: 2,
        },
        sync: {
          enabled: true,
          group: 1,
          suppressTooltips: false
        },
        zoom: {
          enabled: false,
        },
        snap: {
          enabled: true
        }
      }
    },
    hover: {
      mode: 'index',
      intersect: false,
      animationDuration: 0
    },
    // 부드러운 애니메이션 설정
    animation: {
      duration: 800, // 애니메이션 지속 시간 증가
      easing: 'easeInOutQuart' // 부드러운 이징
    },
    responsiveAnimationDuration: 800,
    elements: {
      point: {
        radius: 0,        // 기본 점 크기 0 (보이지 않음)
        hoverRadius: 0,   // 호버 시 점 크기 0 (변화 없음)
        hoverBorderWidth: 0, // 호버 시 테두리 없음
        borderWidth: 0,   // 기본 테두리 없음
      },
      line: {
        tension: 0,  // 부드러운 곡선 제거, 직선으로 연결
      }
    }
  };

  return (
    <div 
      className={`chart-wrapper line-chart smooth-chart ${className} ${chartBorder ? '' : 'no-border'} ${chartPadding ? '' : 'no-padding'}`}
      style={{ height, width }}
    >
      <Line 
        ref={chartRef}
        data={chartData} 
        options={options} 
        plugins={[crosshair]}
      />
    </div>
  );
});

SmoothLineChart.displayName = 'SmoothLineChart';

export default SmoothLineChart;
